import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { KeyRound, Package, Plus } from 'lucide-react';
import { FilterBar, SelectFilter, SearchInput, DateRangeFilter, ClearFilters } from '@/components/filters';
import { buildQuery, type DateRange } from '@/lib/filters';
import { StatTiles } from '@/components/StatTiles';
import { PageHeader } from '@/components/PageHeader';
import { Pagination, DEFAULT_PAGE_SIZE } from '@/components/Pagination';
import { StatusBadge } from '@/components/StatusBadge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataKeysDialog } from '@/components/DataKeysDialog';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  type: string;
  status: string;
  createdAt: number;
}

function Products() {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [dataKeysProduct, setDataKeysProduct] = useState<Product | null>(null);
  const [isDataKeysDialogOpen, setIsDataKeysDialogOpen] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    description: '',
    price: 0,
    type: 'STANDARD',
    status: 'ACTIVE',
  });

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState<DateRange>({});

  useEffect(() => {
    const id = setTimeout(fetchProducts, search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, typeFilter, statusFilter, dateRange.from, dateRange.to, page]);

  // A filter change invalidates the current page — otherwise you can sit on
  // page 5 of a result set that now has one page and see an empty table.
  useEffect(() => { setPage(1); }, [search, typeFilter, statusFilter, dateRange.from, dateRange.to]);

  const clearFilters = () => {
    setSearch('');
    setTypeFilter('all');
    setStatusFilter('all');
    setDateRange({});
  };

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const qs = buildQuery({
        limit: DEFAULT_PAGE_SIZE,
        page,
        search,
        type: typeFilter,
        status: statusFilter,
        from: dateRange.from,
        to: dateRange.to,
      });
      const response = await fetch(`${apiUrl}/api/products${qs}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProducts(data.data || []);
        setTotal(data.total ?? 0);
      }
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProduct = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newProduct),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewProduct({ name: '', description: '', price: 0, type: 'STANDARD', status: 'ACTIVE' });
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to create product:', error);
    }
  };

  const handleEditProduct = async () => {
    if (!selectedProduct) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/products/${selectedProduct.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(selectedProduct),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedProduct(null);
        fetchProducts();
      }
    } catch (error) {
      console.error('Failed to update product:', error);
    }
  };

  const openEditDialog = (product: Product) => {
    setSelectedProduct({ ...product });
    setIsEditDialogOpen(true);
  };

  const openDataKeysDialog = (product: Product) => {
    setDataKeysProduct(product);
    setIsDataKeysDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Catalog"
        icon={Package}
        title={t('products.title')}
        subtitle={t('products.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            {t('products.addProduct')}
          </Button>
        )}
      />

      <StatTiles stats={[
        { label: t('products.stats.matching', { defaultValue: 'Matching results' }), value: total },
        { label: t('products.stats.active', { defaultValue: 'Active on page' }), value: products.filter((p) => p.status === 'ACTIVE').length, color: 'text-success' },
        { label: t('products.stats.inactive', { defaultValue: 'Inactive on page' }), value: products.filter((p) => p.status === 'INACTIVE').length },
        { label: t('products.stats.software', { defaultValue: 'Software on page' }), value: products.filter((p) => p.type === 'SOFTWARE').length },
        { label: t('products.stats.subscription', { defaultValue: 'Subscription on page' }), value: products.filter((p) => p.type === 'SUBSCRIPTION').length },
        { label: t('products.stats.hardware', { defaultValue: 'Hardware on page' }), value: products.filter((p) => p.type === 'HARDWARE').length },
      ]} />

      <Card>
        <CardHeader>
          <CardTitle>{t('products.allProducts')}</CardTitle>
          <CardDescription>
            {t('products.description', { count: total })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FilterBar>
            <SearchInput value={search} onChange={setSearch} placeholder="Name or description…" />
            <SelectFilter
              label={t('products.table.type')}
              value={typeFilter}
              onChange={setTypeFilter}
              options={['SOFTWARE', 'SUBSCRIPTION', 'HARDWARE', 'SERVICE'].map((v) => ({ value: v, label: v }))}
            />
            <SelectFilter
              label={t('products.table.status')}
              value={statusFilter}
              onChange={setStatusFilter}
              options={['ACTIVE', 'INACTIVE', 'DEPRECATED'].map((v) => ({ value: v, label: v }))}
            />
            <DateRangeFilter label="Created" value={dateRange} onChange={setDateRange} />
            <ClearFilters onClick={clearFilters} />
          </FilterBar>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('products.table.name')}</TableHead>
                <TableHead>{t('products.table.description')}</TableHead>
                <TableHead>{t('products.table.price')}</TableHead>
                <TableHead>{t('products.table.type')}</TableHead>
                <TableHead>{t('products.table.status')}</TableHead>
                <TableHead className="text-right">{t('users.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell className="font-medium">{product.name}</TableCell>
                    <TableCell className="max-w-md break-words">{product.description}</TableCell>
                    <TableCell>${product.price.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{product.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={product.status} />
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button variant="ghost" size="sm" onClick={() => openDataKeysDialog(product)}>
                        <KeyRound className="h-4 w-4 mr-2" />
                        Data keys
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(product)}>
                        {t('common.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <Pagination page={page} pageSize={DEFAULT_PAGE_SIZE} total={total} onPageChange={setPage} label="products" />
        </CardContent>
      </Card>

      {/* Create Product Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('products.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('products.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('products.table.name')}</Label>
              <Input
                id="name"
                value={newProduct.name}
                onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                placeholder={t('products.table.name')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">{t('products.table.description')}</Label>
              <Input
                id="description"
                value={newProduct.description}
                onChange={(e) => setNewProduct({ ...newProduct, description: e.target.value })}
                placeholder={t('products.table.description')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="price">{t('products.table.price')}</Label>
              <Input
                id="price"
                type="number"
                value={newProduct.price}
                onChange={(e) => setNewProduct({ ...newProduct, price: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="type">{t('products.table.type')}</Label>
              <Select
                value={newProduct.type}
                onValueChange={(value) => setNewProduct({ ...newProduct, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="STANDARD">Standard</SelectItem>
                  <SelectItem value="PRO">Pro</SelectItem>
                  <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="status">{t('products.table.status')}</Label>
              <Select
                value={newProduct.status}
                onValueChange={(value) => setNewProduct({ ...newProduct, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                  <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateProduct} disabled={!newProduct.name || !newProduct.description}>
              {t('products.createProduct')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('products.dialogs.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('products.dialogs.edit.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedProduct && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('products.table.name')}</Label>
                <Input
                  id="edit-name"
                  value={selectedProduct.name}
                  onChange={(e) => setSelectedProduct({ ...selectedProduct, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-description">{t('products.table.description')}</Label>
                <Input
                  id="edit-description"
                  value={selectedProduct.description}
                  onChange={(e) => setSelectedProduct({ ...selectedProduct, description: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-price">{t('products.table.price')}</Label>
                <Input
                  id="edit-price"
                  type="number"
                  value={selectedProduct.price}
                  onChange={(e) => setSelectedProduct({ ...selectedProduct, price: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-type">{t('products.table.type')}</Label>
                <Select
                  value={selectedProduct.type}
                  onValueChange={(value) => setSelectedProduct({ ...selectedProduct, type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STANDARD">Standard</SelectItem>
                    <SelectItem value="PRO">Pro</SelectItem>
                    <SelectItem value="ENTERPRISE">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-status">{t('products.table.status')}</Label>
                <Select
                  value={selectedProduct.status}
                  onValueChange={(value) => setSelectedProduct({ ...selectedProduct, status: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">{t('licenses.active')}</SelectItem>
                    <SelectItem value="INACTIVE">{t('licenses.inactive')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditProduct}>
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Data Keys Dialog */}
      <DataKeysDialog
        product={dataKeysProduct}
        open={isDataKeysDialogOpen}
        onOpenChange={(open) => {
          setIsDataKeysDialogOpen(open);
          if (!open) setDataKeysProduct(null);
        }}
      />
    </div>
  );
}

export default Products;
